import { doctorFullName, doctorWorkplace } from "../shared/helpers/doctors";
import type { Doctor } from "../shared/types";
import { DoctorStructuredData } from "./DoctorStructuredData";
import Head from "next/head";
import { getFrontendTenantConfigurationById } from "../shared/contexts/tenantsConfigurations";
import { useTranslations } from "../shared/i18n/localization";

interface DoctorProfileHeadProps {
  doctor: Doctor;
  tenantId: string;
}

export const DoctorProfileHead: React.FC<DoctorProfileHeadProps> = ({
  tenantId,
  doctor,
}) => {
  const tenantConfiguration = getFrontendTenantConfigurationById(tenantId);
  const { translate, locale } = useTranslations();
  const doctorName = doctorFullName(doctor);
  const url = `${process.env.hostName}/${locale}/t/${tenantConfiguration.name}/d/${doctor.display_settings?.slug}`;
  const city =
    doctorWorkplace(doctor)?.address?.city ??
    tenantConfiguration.name.toUpperCase();
  const isGkv = doctor.legal_information?.germany?.is_gkv_member ?? false;

  return (
    <>
      <Head>
        <title>
          {translate("doctorProfile.htmlPageTitle", { doctorName, city })}
        </title>
        <meta
          name="description"
          content={translate(
            isGkv
              ? "doctorProfile.metaDescription.gkv"
              : "doctorProfile.metaDescription.default",
            { doctorName }
          )}
        />
        <link rel="canonical" href={url} />
        {tenantConfiguration.enabledPatientLocales.map((supportedLocale) => (
          <link
            key={supportedLocale}
            rel="alternate"
            hrefLang={supportedLocale}
            href={`${process.env.hostName}/${supportedLocale}/t/${tenantConfiguration.name}/d/${doctor.display_settings?.slug}`}
          />
        ))}
        <link rel="icon" href={`/${tenantConfiguration.name}/favicon.svg`} />
        <DoctorStructuredData doctor={doctor} itemUrl={url} />
      </Head>
    </>
  );
};
